import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useMemo } from 'react';
import SearchExperienceService from './searchExperienceService';
import {
  updateConfig,
  updateTheme,
  updateBehavior,
  updateCanvas,
  resetConfig,
  setMode,
  setActiveTab,
  setCurrentView,
  setCurrentScreen,
  setCurrentDevice,
  clearError,
  loadRegistryRequest,
  loadRegistrySuccess,
  loadRegistryFailure,
} from './searchExperienceSlice';
import ComponentRegistry, { loadComponents } from '../core/registry/ComponentRegistry';
import { useLocalizedMessage } from '../../../util/useLocalizedMessage';

/**
 * Custom hook for Search Experience v2
 * Provides all necessary state and actions for components
 *
 * @param {Object} client - Runtime-backed V2 settings client
 * @param {string} shopId - Shop identifier
 */
export const useSearchExperience = (client, shopId) => {
  const t = useLocalizedMessage();
  const dispatch = useDispatch();
  const searchExpState = useSelector((reduxState) => reduxState.searchExperience);

  // Memoize service instance
  const service = useMemo(() => new SearchExperienceService(client, t), [client, t]);

  // Load config
  const loadConfig = useCallback(async (mode = 'preview') => service.loadConfig(shopId, mode, dispatch), [shopId, dispatch, service]);

  // Load schema
  const loadSchema = useCallback(
    async () => service.loadSchema(shopId, dispatch),
    [shopId, dispatch, service],
  );

  // Load component registry
  const loadRegistry = useCallback(async () => {
    try {
      dispatch(loadRegistryRequest());
      ComponentRegistry.initialize(client);
      await loadComponents(shopId);
      dispatch(loadRegistrySuccess());
    } catch (error) {
      // console.error('Failed to load component registry:', error);
      dispatch(loadRegistryFailure({ error: error.message }));
    }
  }, [client, shopId, dispatch]);

  // Save config
  const saveConfig = useCallback(async () => {
    if (!searchExpState.config) return undefined;
    return service.saveConfig(shopId, searchExpState.config, searchExpState.mode, dispatch);
  }, [shopId, searchExpState.config, searchExpState.mode, dispatch, service]);

  // Copy to production
  const copyToProduction = useCallback(
    async () => service.copyToProduction(shopId, dispatch),
    [shopId, dispatch, service],
  );

  // Validate config
  const validateConfig = useCallback(async () => {
    if (!searchExpState.config) return undefined;
    return service.validateConfig(shopId, searchExpState.config, dispatch);
  }, [shopId, searchExpState.config, dispatch, service]);

  // Get diff
  const getDiff = useCallback(() => {
    if (!searchExpState.config || !searchExpState.originalConfig) return null;
    return service.getConfigDiff(searchExpState.originalConfig, searchExpState.config);
  }, [searchExpState.config, searchExpState.originalConfig, service]);

  // Update actions
  const update = useCallback((payload) => {
    dispatch(updateConfig(payload));
  }, [dispatch]);

  const updateThemeConfig = useCallback((payload) => {
    dispatch(updateTheme(payload));
  }, [dispatch]);

  const updateBehaviorConfig = useCallback((payload) => {
    dispatch(updateBehavior(payload));
  }, [dispatch]);

  const updateCanvasConfig = useCallback((payload) => {
    dispatch(updateCanvas(payload));
  }, [dispatch]);

  const reset = useCallback(() => {
    dispatch(resetConfig());
  }, [dispatch]);

  const switchMode = useCallback((mode) => {
    dispatch(setMode({ mode }));
  }, [dispatch]);

  const changeTab = useCallback((tab) => {
    dispatch(setActiveTab({ tab }));
  }, [dispatch]);

  const changeView = useCallback((view) => {
    dispatch(setCurrentView({ view }));
  }, [dispatch]);

  const changeScreen = useCallback((screen) => {
    dispatch(setCurrentScreen({ screen }));
  }, [dispatch]);

  const changeDevice = useCallback((device) => {
    dispatch(setCurrentDevice({ device }));
  }, [dispatch]);

  const clearErr = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  const validateNow = useCallback(async () => {
    if (!searchExpState.config) return { valid: false, errors: [] };
    return service.validateConfig(shopId, searchExpState.config, dispatch);
  }, [shopId, searchExpState.config, dispatch, service]);

  return {
    // State
    ...searchExpState,

    // Computed
    diff: getDiff(),

    // Actions
    loadConfig,
    loadSchema,
    loadRegistry,
    saveConfig,
    copyToProduction,
    validateConfig,
    update,
    updateTheme: updateThemeConfig,
    updateBehavior: updateBehaviorConfig,
    updateCanvas: updateCanvasConfig,
    reset,
    switchMode,
    changeTab,
    changeView,
    changeScreen,
    changeDevice,
    clearError: clearErr,
    validateNow,
  };
};

/**
 * Hook to get only config state (without actions)
 */
export const useSearchExperienceState = () => useSelector((state) => state.searchExperience);

/**
 * Hook to check if config has unsaved changes
 */
export const useHasUnsavedChanges = () => {
  const { isDirty } = useSelector((state) => state.searchExperience);
  return isDirty;
};

/**
 * Hook to get validation state
 */
export const useValidationState = () => {
  const { validationErrors, isValid } = useSelector((state) => state.searchExperience);
  return { validationErrors, isValid };
};
